/**
 * Slide-in chat panel.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Reads chat state from the Zustand chatSlice. Resolves the active site
 * scope from either the explicit `scopeOverride` or the route-derived
 * `activeSiteId`. Dispatches askDam on send + persists the
 * conversation back to local store via appendMessage.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { useStore } from '../../store';
import { auth } from '../../firebase';
import { callUserFunction } from '../../lib/callUserFunction';
import type { AskDamInput, AskDamOutput, Citation } from '../../types/chat';
import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { ChatSiteScopeHeader } from './ChatSiteScopeHeader';
import { availabilityForScope, dataAwareQuestions } from './dataAwareQuestions';

const MODEL_ID = 'gemini-2.5-pro';

interface Props {
  /** Route-detected site (e.g. from `/sites/{id}`). Optional. */
  routeSiteId?: string | null;
  routeSiteLabel?: string | null;
  /** Optional callback for resolving a citation chip click. */
  onResolveCitation?: (citation: Citation) => void;
  /** True when the signed-in user has a companyId on their profile. */
  canShareWithTeam?: boolean;
}

export function ChatPanel({
  routeSiteId,
  routeSiteLabel,
  onResolveCitation,
  canShareWithTeam = false,
}: Props): React.JSX.Element | null {
  const isOpen = useStore((s) => s.isOpen);
  const closeChat = useStore((s) => s.closeChat);
  const scopeOverride = useStore((s) => s.scopeOverride);
  const setScopeOverride = useStore((s) => s.setScopeOverride);
  const activeConversation = useStore((s) => s.activeConversation);
  const startNewConversation = useStore((s) => s.startNewConversation);
  const appendMessage = useStore((s) => s.appendMessage);
  const setFeedback = useStore((s) => s.setFeedback);
  const markCuratedInsight = useStore((s) => s.markCuratedInsight);
  const composerDraft = useStore((s) => s.composerDraft);
  const setComposerDraft = useStore((s) => s.setComposerDraft);
  const isWaiting = useStore((s) => s.isWaiting);
  const setWaiting = useStore((s) => s.setWaiting);
  // Subscribe to the sites ROOT (not a derived selector) — deriving the
  // availability map happens in a useMemo below so useShallow isn't needed.
  const sites = useStore((s) => s.siteRag.sites);

  // Resolve the active scope.
  // scopeOverride === undefined → use route's siteId (auto-detect)
  // scopeOverride === null      → portfolio-wide (explicit)
  // scopeOverride === 'siteId'  → that site (explicit)
  const resolvedSiteId: string | null = useMemo(() => {
    if (scopeOverride === undefined) return routeSiteId ?? null;
    return scopeOverride;
  }, [scopeOverride, routeSiteId]);

  const resolvedScopeLabel: string | null = useMemo(() => {
    if (!resolvedSiteId) return null;
    if (scopeOverride === undefined) return routeSiteLabel ?? resolvedSiteId;
    if (scopeOverride === resolvedSiteId) return routeSiteLabel ?? resolvedSiteId;
    return resolvedSiteId;
  }, [resolvedSiteId, scopeOverride, routeSiteLabel]);

  // Data-aware starter questions: only surface a prompt chip when its
  // underlying data exists for the active site / portfolio (e.g. hide the
  // LCOE-ranking question when no sites have a sim/LCOE run). Derived in a
  // useMemo off the subscribed `sites` root for selector stability.
  const exampleQuestions = useMemo(() => {
    const { scope, map } = availabilityForScope(resolvedSiteId, sites);
    return dataAwareQuestions(scope, map);
  }, [resolvedSiteId, sites]);

  // Bootstrap a conversation when the panel opens.
  useEffect(() => {
    if (!isOpen) return;
    if (activeConversation) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    startNewConversation(uid, MODEL_ID, resolvedSiteId);
  }, [isOpen, activeConversation, resolvedSiteId, startNewConversation]);

  const handleSend = useCallback(async (text: string) => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      toast.error('Sign in to chat');
      return;
    }
    if (!activeConversation) {
      startNewConversation(uid, MODEL_ID, resolvedSiteId);
    }
    const conv = useStore.getState().activeConversation!;
    const now = new Date().toISOString();
    appendMessage({
      id: `local-${crypto.randomUUID()}`,
      role: 'user',
      content: text,
      createdAt: now,
    });
    setComposerDraft('');
    setWaiting(true);
    try {
      // Routed through the server proxy (`/api/functions/askDam`) — direct
      // browser → Cloud Run callable invocation is blocked by the org's Domain
      // Restricted Sharing policy. See src/lib/callUserFunction.ts.
      const data = await callUserFunction<AskDamOutput>('askDam', {
        conversationId: conv.id,
        message: text,
        siteId: resolvedSiteId,
        history: conv.messages,
      } satisfies AskDamInput);
      appendMessage(data.message);
      if (data.truncated) {
        toast.info('Context was trimmed to fit — answer may miss some details');
      }
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? 'Could not reach the assistant');
    } finally {
      setWaiting(false);
    }
  }, [activeConversation, appendMessage, resolvedSiteId, setComposerDraft, setWaiting, startNewConversation]);

  const handleFeedbackUpdate = useCallback(
    (messageId: string, update: { feedback?: 'positive' | 'negative'; curatedInsightId?: string }) => {
      if (update.feedback) setFeedback(messageId, update.feedback);
      if (update.curatedInsightId) markCuratedInsight(messageId, update.curatedInsightId);
    },
    [setFeedback, markCuratedInsight],
  );

  if (!isOpen) return null;

  return (
    <aside
      className="fixed inset-y-0 right-0 z-50 w-full sm:w-[440px] bg-background border-l-2 border-border shadow-2xl flex flex-col font-mono"
      role="complementary"
      aria-label="Datacrate chat assistant"
      data-testid="chat-panel"
    >
      <header className="flex items-center justify-between px-3 py-2 border-b-2 border-border">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-text-primary">
            Datacrate
          </p>
          <p className="text-[8px] uppercase tracking-widest text-text-muted">
            Assistant
          </p>
        </div>
        <button
          type="button"
          onClick={closeChat}
          aria-label="Close chat"
          className="text-text-muted hover:text-text-primary"
          data-testid="chat-close"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <ChatSiteScopeHeader
        scopeLabel={resolvedScopeLabel}
        isAutoDetected={scopeOverride === undefined && !!routeSiteId}
        routeScopeLabel={
          scopeOverride === null && routeSiteId ? routeSiteLabel ?? routeSiteId : null
        }
        onClearScope={() => setScopeOverride(null)}
        onRestoreRouteScope={() => setScopeOverride(undefined)}
      />

      <ChatMessageList
        messages={activeConversation?.messages ?? []}
        conversationId={activeConversation?.id ?? null}
        canShareWithTeam={canShareWithTeam}
        isWaiting={isWaiting}
        scopeLabel={resolvedScopeLabel}
        exampleQuestions={exampleQuestions}
        onResolveCitation={onResolveCitation}
        onFeedbackUpdate={handleFeedbackUpdate}
        onExampleClick={(text) => void handleSend(text)}
      />

      <ChatInput
        value={composerDraft}
        onChange={setComposerDraft}
        onSubmit={handleSend}
        isWaiting={isWaiting}
        placeholder={
          resolvedScopeLabel
            ? `Ask about ${resolvedScopeLabel}…`
            : 'Ask about your portfolio…'
        }
      />
    </aside>
  );
}
